import { useState } from 'preact/hooks';
import { BaseDialog } from '@components/dialog';
import { useBodyScrollLock } from 'client/lib/hooks/use-body-scroll-lock';
import { BtnPrimary } from '@components/buttons';
import { IcoChat, IcoVideoCamera, IcoAcademicCap, IcoSparkles } from '@components/icons';
import { MiraseeLogo } from '@course/components/mirasee-logo';

const WELCOME_KEY = 'aip-welcome-seen';

type Step = 'welcome' | 'explore' | 'start';

function StepDot({ active }: { active: boolean }) {
  return (
    <span class={`inline-block w-2 h-2 rounded-full ${active ? 'bg-[#003DA6]' : 'bg-gray-300'}`} />
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }) {
  return (
    <div class="flex flex-col items-center text-center gap-6 py-4">
      <MiraseeLogo class="scale-150" />
      <div class="flex flex-col gap-3">
        <h2 class="text-2xl font-semibold text-gray-900">Welcome to AI Playground</h2>
        <p class="text-gray-600 max-w-sm">
          You've joined a community of coaches, consultants, and course creators who are embracing
          AI to grow their businesses and better serve their audiences.
        </p>
      </div>
      <BtnPrimary class="rounded-full px-8 bg-[#003DA6] hover:bg-[#002d7a]" onClick={onNext}>
        Show me around
      </BtnPrimary>
    </div>
  );
}

function ExploreStep({ onNext }: { onNext: () => void }) {
  return (
    <div class="flex flex-col items-center text-center gap-6 py-4">
      <h2 class="text-xl font-semibold text-gray-900">Here's what you'll find</h2>
      <ul class="flex flex-col gap-4 text-left w-full max-w-sm">
        <li class="flex gap-3 items-start">
          <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-[#003DA6]">
            <IcoChat class="w-5 h-5" />
          </span>
          <span>
            <span class="font-medium text-gray-900 block">Community Discussions</span>
            <span class="text-sm text-gray-500">
              Share wins, ask questions, and connect with fellow entrepreneurs
            </span>
          </span>
        </li>
        <li class="flex gap-3 items-start">
          <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-[#00ABC8]">
            <IcoVideoCamera class="w-5 h-5" />
          </span>
          <span>
            <span class="font-medium text-gray-900 block">Live Calls</span>
            <span class="text-sm text-gray-500">
              Join regular sessions with the Mirasee team and community members
            </span>
          </span>
        </li>
        <li class="flex gap-3 items-start">
          <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-[#FF6B00]">
            <IcoAcademicCap class="w-5 h-5" />
          </span>
          <span>
            <span class="font-medium text-gray-900 block">Courses & Resources</span>
            <span class="text-sm text-gray-500">
              Access training materials and tools to help you leverage AI in your business
            </span>
          </span>
        </li>
        <li class="flex gap-3 items-start">
          <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-green-50 text-[#70D44B]">
            <IcoSparkles class="w-5 h-5" />
          </span>
          <span>
            <span class="font-medium text-gray-900 block">AI Playground</span>
            <span class="text-sm text-gray-500">
              Your home base for exploring what's possible with AI as an entrepreneur
            </span>
          </span>
        </li>
      </ul>
      <BtnPrimary class="rounded-full px-8 bg-[#003DA6] hover:bg-[#002d7a]" onClick={onNext}>
        Got it
      </BtnPrimary>
    </div>
  );
}

function StartStep({ onDone }: { onDone: () => void }) {
  return (
    <div class="flex flex-col items-center text-center gap-6 py-4">
      <span class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#003DA6]/10 text-[#003DA6]">
        <IcoChat class="w-7 h-7" />
      </span>
      <div class="flex flex-col gap-3">
        <h2 class="text-xl font-semibold text-gray-900">Ready to dive in?</h2>
        <p class="text-gray-600 max-w-sm">
          Start by introducing yourself in the community. Share who you are, what you do, and what
          you're hoping to get out of AI Playground. We'd love to get to know you!
        </p>
      </div>
      <BtnPrimary class="rounded-full px-8 bg-[#003DA6] hover:bg-[#002d7a]" onClick={onDone}>
        Let's go
      </BtnPrimary>
    </div>
  );
}

export function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(() => {
    return localStorage.getItem(WELCOME_KEY) !== 'true';
  });
  const [step, setStep] = useState<Step>('welcome');

  useBodyScrollLock(isOpen);

  if (!isOpen) {
    return undefined;
  }

  function dismiss() {
    localStorage.setItem(WELCOME_KEY, 'true');
    setIsOpen(false);
  }

  const steps: Step[] = ['welcome', 'explore', 'start'];

  return (
    <BaseDialog onClose={dismiss}>
      <div class="p-8">
        {step === 'welcome' && <WelcomeStep onNext={() => setStep('explore')} />}
        {step === 'explore' && <ExploreStep onNext={() => setStep('start')} />}
        {step === 'start' && <StartStep onDone={dismiss} />}
        <div class="flex justify-center gap-2 mt-4">
          {steps.map((s) => (
            <StepDot key={s} active={s === step} />
          ))}
        </div>
      </div>
    </BaseDialog>
  );
}
